import React, { useContext, useCallback, useMemo } from 'react';

import { useSelector, useDispatch } from 'react-redux';

import { noop } from 'ui-components/default-props/noop';
import { CreateReturnButtonId, ExportShipmentsButtonId } from './grid-available-buttons';
import OverflowDropdown from '../../components/grid-header/overflow-dropdown';
import { emptyArray, emptyObject } from 'ui-components/default-props/empty';
import { FiltersContext } from '../../filters/context';
import { NewReturnFulfillmentId } from '../../orders/order-details/hooks/use-pending-return';
import { useReturnNavigation } from '../../orders/order-details/hooks/use-return-navigation';
import { bulkVoidStart } from '../../store/bulk/void/bulk.void.actions';
import { openExportShipmentsModal } from '../../store/export/shipments/export-shipments.actions';
import Global from '../../store/global-selectors';
import {
  refreshTracking as refreshTrackingAction,
  sendShipmentNotification,
  notifyMarketplace as notifyMarketplaceAction,
} from '../../store/shipments/grid/shipments.grid.header.actions';
import { useSelectedShippingViewId } from './shipping-left-nav/shipping-left-nav-context';
import { useCanCreateEnhancedReturn } from '../../store/returns/returns.helpers';
import { useTrackWorkflow, WorkflowSteps } from '../../tracking/use-workflow-tracking';
import { EventSourceStrings, Workflows } from '../../tracking/tracking-constants';
import { useDataExportFeatureGate } from '../../feature-gates/use-data-export-feature-gate';
import { useShipmentsGridAvailableButtonsForView } from './use-shipments-grid-available-buttons-for-view';

/**
 * @param {object} props
 * @param {[]} props.hiddenComponents
 * @returns {React.JSX.Element}
 */
export const ShippingPageOverflowDropdownWrapper = ({ hiddenComponents }) => {
  const dispatch = useDispatch();
  const filterContext = useContext(FiltersContext);

  const buildApiFilters = filterContext && filterContext.buildApiFilters ? filterContext.buildApiFilters : noop;
  const selectedFulfillmentIds = useSelector(Global.selectSelectedShipmentsSelectedShipmentIds) || emptyArray;
  const oneOrMoreShipmentSelected = selectedFulfillmentIds.length >= 1;
  const oneShipmentSelected = selectedFulfillmentIds.length === 1;
  const selectedFulfillmentId = selectedFulfillmentIds[0];
  const [selectedFulfillmentPlanId] =
    useSelector((state) =>
      Global.selectEntitiesFPIdsByFulfillmentIds(state, {
        fulfillmentIds: selectedFulfillmentId ? [selectedFulfillmentId] : [],
      }),
    ) || emptyArray;
  const selectedFulfillmentPlan =
    useSelector((state) =>
      Global.selectEntitiesFulfillmentPlanById(state, {
        fulfillmentPlanId: selectedFulfillmentPlanId,
      }),
    ) || emptyObject;
  const { salesOrdersFulfilledIds = [] } = selectedFulfillmentPlan;
  const salesOrderId = salesOrdersFulfilledIds[0];
  const quickSearchPerformed = useSelector(Global.selectShipmentsNavSearchPerformed);
  const shouldUseEnhancedReturn = useCanCreateEnhancedReturn(salesOrderId);

  const { isFeatureEnabled: isDataExportEnabled, disabledMessage: dataExportDisabledMessage } = useDataExportFeatureGate();

  const viewId = useSelectedShippingViewId();
  const buttonObjects = useShipmentsGridAvailableButtonsForView(viewId);
  const actions = useMemo(
    () =>
      buttonObjects.reduce((acc, button) => {
        const buttonId = button.id;

        let isButtonDisabled;
        let disabledReason;

        if (buttonId === CreateReturnButtonId) {
          isButtonDisabled = !oneShipmentSelected || !salesOrderId;
        } else if (buttonId === ExportShipmentsButtonId) {
          isButtonDisabled = !isDataExportEnabled;
          disabledReason = dataExportDisabledMessage;
        } else {
          isButtonDisabled = !oneOrMoreShipmentSelected;
        }

        return {
          ...acc,
          [buttonId]: {
            disabled: isButtonDisabled,
            disabledReason,
          },
        };
      }, {}),
    [buttonObjects, dataExportDisabledMessage, isDataExportEnabled, oneOrMoreShipmentSelected, oneShipmentSelected, salesOrderId],
  );

  const voidLabel = useCallback((fulfillmentIds) => dispatch(bulkVoidStart(fulfillmentIds)), [dispatch]);
  const refreshTracking = useCallback((fulfillmentIds) => dispatch(refreshTrackingAction(fulfillmentIds)), [dispatch]);
  const sendNotifications = useCallback((fulfillmentIds) => dispatch(sendShipmentNotification(fulfillmentIds)), [dispatch]);
  const exportShipments = useCallback(() => dispatch(openExportShipmentsModal(buildApiFilters)), [buildApiFilters, dispatch]);
  const notifyMarketplace = useCallback((fulfillmentIds) => dispatch(notifyMarketplaceAction(fulfillmentIds)), [dispatch]);

  const { navigateToReturn } = useReturnNavigation();
  const trackCreateReturn = useTrackWorkflow(Workflows.CREATE_RETURN_FLOW);

  const createReturn = useCallback(() => {
    trackCreateReturn(WorkflowSteps.START, {
      enhanced: shouldUseEnhancedReturn,
      fulfillmentPlanId: selectedFulfillmentPlanId,
      salesOrderId,
      source: EventSourceStrings.SHIPMENTS_GRID_HEADER,
    });
    navigateToReturn(salesOrderId, NewReturnFulfillmentId, shouldUseEnhancedReturn);
  }, [navigateToReturn, salesOrderId, selectedFulfillmentPlanId, shouldUseEnhancedReturn, trackCreateReturn]);

  const actionHandlers = useMemo(
    () => ({
      voidLabel,
      refreshTracking,
      sendNotifications,
      exportShipments,
      createReturn,
      notifyMarketplace,
    }),
    [voidLabel, refreshTracking, sendNotifications, exportShipments, createReturn, notifyMarketplace],
  );

  return (
    <OverflowDropdown
      actionHandlers={actionHandlers}
      actions={actions}
      hiddenComponents={hiddenComponents}
      isShipmentsPage
      quickSearchPerformed={quickSearchPerformed}
      selectedIds={selectedFulfillmentIds}
    />
  );
};
